import './dropDowns.css';

import React from 'react';
import { useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../../recoil/atoms/userConfigState';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

import { translate } from '../../../../common/language/translations';
import { minConfidenceKeys } from '../../../static/constants/scoutingFilters';


interface ConfidenceDropDownProps {
  id: string;

  selectedOptionsSkillIndex: string[];
  selectedOptionsAllIndexes: string[];
  setSelectedOptions: (value: string[], value2: string[]) => void;

  isDropDownExpanded: boolean;
  setIsDropDownExpanded: (value: boolean) => void;

  defaultDropDownText: string; // static language key
  defaultDropDownTextColor: string;
  marginBetweenOptions: number;
  height?: number;
  maxHeight?: string | number; // controls scrolling
}

export const ConfidenceDropDown: React.FC<ConfidenceDropDownProps> = ({
  id,

  selectedOptionsSkillIndex,
  selectedOptionsAllIndexes,
  setSelectedOptions,

  isDropDownExpanded,
  setIsDropDownExpanded,

  defaultDropDownText,
  defaultDropDownTextColor,
  marginBetweenOptions,
  height,
  maxHeight,
}) => {

  const userConfig = useRecoilValue(userConfigState);

  const dropDownRef = useRef<HTMLDivElement>(null);


  const handleDropDownClick = () => {
    if (isDropDownExpanded) {
      removeDropDownExpansion();
    }

    else {
      expandDropDown();
    }
  };


  const handleOptionSelect = (option: string, isOptionAlreadySelected: boolean, isSkillConfidence: boolean) => {

    let newSelectedOptionsSkillIndex: string[] = selectedOptionsSkillIndex.slice();
    let newSelectedOptionsAllIndexes: string[] = selectedOptionsAllIndexes.slice();

    if (isOptionAlreadySelected) {
      if (isSkillConfidence) {
        newSelectedOptionsSkillIndex = [];
      }
      else {
        newSelectedOptionsAllIndexes = [];
      }
    }

    else {
      if (isSkillConfidence) {
        newSelectedOptionsSkillIndex = [option];
      }
      else {
        newSelectedOptionsAllIndexes = [option];

        // if the new selected all indexes confidence is greater than the selected skill index confidence, we reset the skill index confidence
        if (Number(option) >= Number(selectedOptionsSkillIndex[0])) {
          newSelectedOptionsSkillIndex = [];
        }
      }
    }

    setSelectedOptions(newSelectedOptionsSkillIndex, newSelectedOptionsAllIndexes);
  };


  const expandDropDown = () => {
    const element = document.getElementById(id);
    if (element) {
      element.style.transition = 'height 150ms';
      element.style.height = 'auto';
      element.style.zIndex = '100';
    }
    setIsDropDownExpanded(true);
  };


  const removeDropDownExpansion = () => {
    const element = document.getElementById(id);
    if (element) {
      element.style.transition = '75ms';
      element.style.height = height ? height + 'px' : '28px';
      element.style.zIndex = '1';
    }
    setIsDropDownExpanded(false);
    if (dropDownRef.current) {
      dropDownRef.current.scrollTop = 0;
    }
  };


  const showSelectedOptions = () => {
    if (!userConfig) return '';

    const selectedSkillIndexString = selectedOptionsSkillIndex.length > 0
      // ? (translate('skillIndex', userConfig?.language) + ': ' + selectedOptionsSkillIndex[0])
      ? selectedOptionsSkillIndex[0]
      : '';
    const selectedAllIndexesString = selectedOptionsAllIndexes.length > 0
      // ? (translate('allIndexes', userConfig?.language) + ': ' + selectedOptionsAllIndexes[0])
      ? selectedOptionsAllIndexes[0]
      : '';

    // we first show the selected positions, then a divider, then the selected roles
    return (
      <div className='flex-row' style={{ color: '#000000' }}>
        {selectedSkillIndexString}

        {selectedSkillIndexString.length > 0 && selectedAllIndexesString.length > 0 && (
          <div
            style={{
              height: '100%',
              width: 1,
              backgroundColor: '#000000cc',
              marginRight: 8,
              marginLeft: 8
            }}>
            &nbsp;
          </div>
        )}

        {selectedAllIndexesString}
      </div>
    );
  };


  useEffect(() => {
    if (!isDropDownExpanded) {
      removeDropDownExpansion();
    }
  }, [isDropDownExpanded]); // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <div
      className='drop-down-container'
      id={id}
      style={{ height: height, maxHeight: maxHeight }}
    >

      <div
        className='drop-down-button'
        style={{ height: height }}
        onClick={() => handleDropDownClick()}
      >

        {(selectedOptionsSkillIndex.length > 0 || selectedOptionsAllIndexes.length > 0) && <div className='drop-down-button-overlay' />}

        <div className='drop-down-button-text'>
          {(selectedOptionsSkillIndex.length === 0 && selectedOptionsAllIndexes.length === 0) ? (
            <div style={{ color: defaultDropDownTextColor }}>
              {translate(defaultDropDownText, userConfig?.language)}
            </div>
          ) : (
            <div style={{ color: '#000000' }}>
              {showSelectedOptions()}
            </div>
          )}
        </div>

        {!isDropDownExpanded && (
          <div className='drop-down-icon' style={{ marginTop: height ? (height - 24) / 2 : undefined }}>
            <ArrowDropDownIcon style={{ fontSize: 24 }} />
          </div>
        )}

        {isDropDownExpanded && (
          <div className='drop-down-icon' style={{ marginTop: height ? (height - 24) / 2 : undefined }}>
            <ArrowDropUpIcon style={{ fontSize: 24 }} />
          </div>
        )}
      </div>

      <div className='drop-down-space'>&nbsp;</div>

      <div
        ref={dropDownRef}
        className={'drop-down-options' + (maxHeight ? ' drop-down-options-scroll' : '')}
        style={{ maxHeight: maxHeight }}
      >
        <div className='metric-drop-down-divider' style={{ marginTop: 10 }}>&nbsp;</div>

        <div className='metric-drop-down-group-title-container'>
          <div className='metric-drop-down-group-title' style={{ textWrap: 'nowrap' }}>
            {translate('skillIndex', userConfig?.language)}
          </div>
        </div>

        {minConfidenceKeys.map(option => {

          const isSelected = selectedOptionsSkillIndex.includes(option);

          // skill index confidences below the selected all indexes confidence are disabled
          const isDisabled = selectedOptionsAllIndexes.length > 0 && Number(option) <= Number(selectedOptionsAllIndexes[0]);

          return (
            <div
              className={
                'drop-down-option'
                + (isSelected ? ' drop-down-option-selected' : '')
                + (isDisabled ? ' drop-down-option-disabled' : '')
              }
              style={{ marginTop: marginBetweenOptions, color: isDisabled ? '#00000060' : '#000000' }}
              key={option}
              onClick={() => !isDisabled && handleOptionSelect(option, isSelected, true)}
            >
              {translate(option, userConfig?.language)}
            </div>
          );
        })}

        <div className='metric-drop-down-divider' style={{ marginTop: 30 }}>&nbsp;</div>

        <div className='metric-drop-down-group-title-container'>
          <div className='metric-drop-down-group-title' style={{ textWrap: 'nowrap' }}>
            {translate('allIndexes', userConfig?.language)}
          </div>
        </div>

        {minConfidenceKeys.map(option => {

          const isSelected = selectedOptionsAllIndexes.includes(option);

          return (
            <div
              className={
                'drop-down-option'
                + (isSelected ? ' drop-down-option-selected' : '')
              }
              style={{ marginTop: marginBetweenOptions }}
              key={option}
              onClick={() => handleOptionSelect(option, isSelected, false)}
            >
              {translate(option, userConfig?.language)}
            </div>
          );
        })}
      </div>
    </div>
  );
};
